import { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Database, Shield, Mail, Bell } from 'lucide-react';
import { apiService } from '../../services/api';

export default function AdminSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    siteName: 'The Gandi Mall',
    siteDescription: 'Premium shopping destination in the heart of the city',
    contactEmail: 'admin@gandimall.com',
    contactPhone: '+1 (555) 123-4567',
    businessHours: '9:00 AM - 10:00 PM',
    maintenanceMode: false,
    emailNotifications: true,
    smsNotifications: false,
    autoApproveApplications: false,
    maxApplicationsPerUser: 3,
    applicationExpiryDays: 30,
    rentIncreasePercentage: 5,
    latePaymentFee: 50,
    securityDepositPercentage: 20
  });

  const [notifications, setNotifications] = useState({
    newApplication: true,
    applicationStatusChange: true,
    paymentReminder: true,
    systemMaintenance: true,
    weeklyReport: false,
    monthlyReport: true
  });


  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getSettings();
      
      if (response.status === 'success' && response.data) {
        const { system, business, lease, notifications: notifSettings } = response.data;
        
        // Map backend settings to frontend format
        setSettings({
          siteName: business?.name || 'The Gandi Mall',
          siteDescription: 'Premium shopping destination in the heart of the city',
          contactEmail: business?.email || 'admin@gandimall.com',
          contactPhone: business?.phone || '+1 (555) 123-4567',
          businessHours: '9:00 AM - 10:00 PM',
          maintenanceMode: system?.maintenanceMode || false,
          emailNotifications: notifSettings?.emailNotifications || true,
          smsNotifications: notifSettings?.smsNotifications || false,
          autoApproveApplications: false,
          maxApplicationsPerUser: 3,
          applicationExpiryDays: 30,
          rentIncreasePercentage: lease?.defaultTerms?.lateFeePercentage || 5,
          latePaymentFee: 50,
          securityDepositPercentage: lease?.defaultTerms?.depositPercentage || 20
        });

        setNotifications({
          newApplication: notifSettings?.applicationAlerts || true,
          applicationStatusChange: true,
          paymentReminder: notifSettings?.leaseExpiryAlerts || true,
          systemMaintenance: true,
          weeklyReport: false,
          monthlyReport: true
        });

      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Using default values.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Map frontend settings to backend format
      const backendSettings = {
        system: {
          name: 'Gandi Mall Management System',
          version: '1.0.0',
          maintenanceMode: settings.maintenanceMode
        },
        business: {
          name: settings.siteName,
          address: '33 Magamba Way, Chinhoyi',
          phone: settings.contactPhone,
          email: settings.contactEmail
        },
        lease: {
          defaultTerms: {
            minimumLeasePeriod: 12,
            depositPercentage: settings.securityDepositPercentage,
            lateFeePercentage: settings.rentIncreasePercentage,
            gracePeriod: 5
          }
        },
        notifications: {
          emailNotifications: settings.emailNotifications,
          smsNotifications: settings.smsNotifications,
          applicationAlerts: notifications.newApplication,
          leaseExpiryAlerts: notifications.paymentReminder
        }
      };

      const response = await apiService.updateSettings(backendSettings);
      
      if (response.status === 'success') {
        setSuccess('Settings saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings({
        siteName: 'The Gandi Mall',
        siteDescription: 'Premium shopping destination in the heart of the city',
        contactEmail: 'admin@gandimall.com',
        contactPhone: '+1 (555) 123-4567',
        businessHours: '9:00 AM - 10:00 PM',
        maintenanceMode: false,
        emailNotifications: true,
        smsNotifications: false,
        autoApproveApplications: false,
        maxApplicationsPerUser: 3,
        applicationExpiryDays: 30,
        rentIncreasePercentage: 5,
        latePaymentFee: 50,
        securityDepositPercentage: 20
      });
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">System Settings</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600">Manage your mall's configuration and preferences</p>
          
          {/* Error/Success Messages */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {success && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Business Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Business Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={settings.siteName}
                    onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={settings.contactEmail}
                    onChange={(e) => setSettings({...settings, contactEmail: e.target.value})}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                    placeholder="Enter contact email"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={settings.contactPhone}
                    onChange={(e) => setSettings({...settings, contactPhone: e.target.value})}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                    placeholder="Enter contact phone"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Business Hours
                  </label>
                  <input
                    type="text"
                    value={settings.businessHours}
                    onChange={(e) => setSettings({...settings, businessHours: e.target.value})}
                    className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                    placeholder="e.g., 9:00 AM - 10:00 PM"
                  />
                </div>
              </div>
              <div className="mt-3 sm:mt-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  Business Description
                </label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({...settings, siteDescription: e.target.value})}
                  rows={3}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                  placeholder="Enter a description of your business"
                />
              </div>
            </div>

            {/* Application Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Applications Per User
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.maxApplicationsPerUser}
                    onChange={(e) => setSettings({...settings, maxApplicationsPerUser: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Expiry (Days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={settings.applicationExpiryDays}
                    onChange={(e) => setSettings({...settings, applicationExpiryDays: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rent Increase Percentage
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="0.1"
                    value={settings.rentIncreasePercentage}
                    onChange={(e) => setSettings({...settings, rentIncreasePercentage: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Payment Fee ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.latePaymentFee}
                    onChange={(e) => setSettings({...settings, latePaymentFee: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.autoApproveApplications}
                    onChange={(e) => setSettings({...settings, autoApproveApplications: e.target.checked})}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Auto-approve applications</span>
                </label>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Settings</h2>
              <div className="space-y-4">
                {Object.entries(notifications).map(([key, value]) => (
                  <label key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setNotifications({...notifications, [key]: e.target.checked})}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </label>
                ))}
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* System Status */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="flex items-center gap-1 text-green-600">
                    <Database className="h-4 w-4" />
                    Connected
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Security</span>
                  <span className="flex items-center gap-1 text-green-600">
                    <Shield className="h-4 w-4" />
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Maintenance</span>
                  <span className={`flex items-center gap-1 ${settings.maintenanceMode ? 'text-red-600' : 'text-green-600'}`}>
                    <Settings className="h-4 w-4" />
                    {settings.maintenanceMode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleReset}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset to Defaults
                </button>
                <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  <Mail className="h-4 w-4" />
                  Send Test Email
                </button>
                <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  <Bell className="h-4 w-4" />
                  Test Notifications
                </button>
              </div>
            </div>

            {/* Save Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="space-y-3">
                <button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  onClick={handleReset}
                  className="w-full text-gray-600 hover:text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
